/**
 * messageDisplay.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import bluebird from "bluebird";
import hljs from "highlight.js";
import { DateTime } from "luxon";
import moment from "moment-timezone";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";

import AddReactionIcon from "@mui/icons-material/AddReaction";
import ChatIcon from "@mui/icons-material/Chat";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { Button, ButtonGroup } from "@mui/material";

import { ReactionsContainer } from "./reactionsContainer.component";
import { SCAvatar } from "../components/avatar.component";

import { Membership } from "../models/auth";
import { ContentType, ContentMessage,
  DisplayMessage,
  updateCachedMemberNameAsNeeded } from "../models/message";
import { Reaction } from "../models/reaction";

import { authServiceFactory } from "../services/auth.service";
import { dbServiceFactory } from "../services/db.service";
import { sanitize } from "../services/purify.service";

interface MessageDisplayComponentProps {
  msg: ContentMessage,
  inThread: boolean,
  displayTZ: string,
  canWriteMessages: boolean | null,
  maintainHover: boolean,
  myUserID: string,
  forceReactionContainer?: boolean,
  doRemoveMessage?(_id: string, frameID: string | undefined, threadID: string | undefined): void,
  doRemoveLocally?(_id: string, frameID: string | undefined, threadID: string | undefined): void,
  doThread?(msgID: string): void,
  doDownloadFile?(fileID: string, keyID: string): void,
  showEmojiPicker?(anchor: HTMLElement, msg: ContentMessage): void,
  doToggleReaction(msg: ContentMessage, reaction: Reaction): void
}

export function MessageDisplayComponent(props: MessageDisplayComponentProps): React.ReactElement<any, any> {
  const { msg } = props;
  const [acctMember, setAcctMember] = React.useState<Membership | null>(null);
  const contentRef = React.useRef(null);

  React.useEffect(function () {
    authServiceFactory().memberFor(msg.userID).then( mem => {
      setAcctMember(mem);
      if (updateCachedMemberNameAsNeeded(msg, mem)) { return; }
      if (!mem) { return; }
      const dbService = dbServiceFactory();
      return msg.threadID ?
        dbService.updateReply(msg._id, updateMsgNameCB) :
        dbService.updateMessage(msg._id, mem.accountID, updateMsgNameCB);
    }).then(null, err => {
      console.error("Error getting membership:", err);
    });
  }, []);

  let content: React.ReactNode;

  function clickHandler (event: React.SyntheticEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".fa-download")) { return; }
    const fileBox = target.closest(".file-attachment-box") as HTMLElement;
    if (!fileBox) {
      console.log("Didn't find file-attachment-box");
      return;
    }
    const keyID = fileBox.dataset.keyId;
    const fileID = fileBox.dataset.fileId;
    if (keyID && fileID && props.doDownloadFile) {
      props.doDownloadFile(fileID, keyID);
    } else {
      console.log("no key, file, or callback", keyID, fileID, props.doDownloadFile);
    }
  }

  function updateMsgNameCB (message: DisplayMessage | null): Promise<DisplayMessage | null> {
    if (message) {
      if (msg.firstname) { message.firstname = msg.firstname; }
      if (msg.lastname) { message.lastname = msg.lastname; }
    }
    return bluebird.resolve(message);
  }

  function updateMsgContentCB (message: DisplayMessage | null): Promise<DisplayMessage | null> {
    if (message) {
      (message as ContentMessage).sanitizedContent = msg.sanitizedContent;
    }
    return bluebird.resolve(message);
  }
  function saveSanitizedContent() {
    const dbService = dbServiceFactory();
    const prom: Promise<DisplayMessage | null> = msg.threadID ?
      dbService.updateReply(msg._id, updateMsgContentCB) :
      dbService.updateMessage(msg._id, acctMember?.accountID || "", updateMsgContentCB);
    prom.catch(err => {
      console.error("Error saving sanitized content:", err);
    });
  }

  const contentType = msg.contenttype || (msg as any).contenttype;

  switch (contentType) {
    case ContentType.html:
      if (!msg.sanitizedContent) {
        const authService = authServiceFactory();
        msg.sanitizedContent = sanitize(msg.content, authService.currentAccountMembership?.userID);
        saveSanitizedContent();
      }
      content = <article ref={contentRef} className="message-content" onClick={clickHandler}
        dangerouslySetInnerHTML={{__html: msg.sanitizedContent as string}}></article>;
      break;
    case ContentType.markdown:
      content = <article ref={contentRef} className="message-content" onClick={clickHandler}>
        <ReactMarkdown remarkPlugins={[gfm]} children={msg.content} />
      </article>;
      break;
    case ContentType.plain:
    default:
      if (!msg.sanitizedContent) {
        const authService = authServiceFactory();
        let content = msg.content.replace(/\n/g, "<br />");
        content = `<p>${content}</p>`;
        msg.sanitizedContent = sanitize(content, authService.currentAccountMembership?.userID);
        saveSanitizedContent();
      }
      content = <article ref={contentRef} className="message-content" onClick={clickHandler}
        dangerouslySetInnerHTML={{__html: msg.sanitizedContent as string}}></article>;
  }

  React.useEffect(function () {
    if (contentRef.current) {
      (contentRef.current as any)?.querySelectorAll("pre code").forEach((el: HTMLElement) => {
        hljs.highlightElement(el);
      });
    }
  }, [msg.content]);

  const firstname = acctMember?.firstname || msg.firstname || "";
  const lastname = acctMember?.lastname || msg.lastname || "";

  const ts = msg.sent || msg.created;
  const timestamp = moment.isMoment(ts) ? DateTime.fromJSDate(ts.toDate()) :
    ts instanceof Date ? DateTime.fromJSDate(ts) :
    ts;
  if (props.displayTZ) {
    timestamp.setZone(props.displayTZ);
  }
  const classes = "group-message-container" + (props.maintainHover ? " maintain-hover" : "");
  return <div className={classes} data-user-id={msg.userID} data-msg-id={msg._id} data-frame-id={msg.frameID}>
    <SCAvatar text={acctMember?.avatarText || "XX"} url={acctMember?.avatarUrl} />
    <header className="group-message-header">
      {firstname || lastname ?
        <span className="message-author">{firstname} {lastname}</span> :
        <span className="message-author">unknown</span>}
      <span> </span>
      <span className="creation-time">
        {props.inThread ?
          timestamp.toRelative() :
          timestamp.toFormat("h:mma")}
      </span>
    </header>
    {content}

    { props.showEmojiPicker && <ReactionsContainer message={msg} myUserID={props.myUserID}
      forceShow={props.forceReactionContainer}
      toggleReaction={props.doToggleReaction}
      showEmojiPicker={props.showEmojiPicker} />}

    {msg.replyCount > 0 && !props.inThread && makeReplyIndicator(msg, props)}

    {makeButtons(msg, props)}
  </div>;
}

function makeReplyIndicator(message: ContentMessage, props: MessageDisplayComponentProps): React.ReactElement<any, any> {
  const label = message.replyCount > 1 ? "replies" : "reply";
  const lastreply: DateTime | undefined = message.lastReply && (
    DateTime.isDateTime(message.lastReply) ? message.lastReply :
    moment.isMoment(message.lastReply) ? DateTime.fromJSDate(message.lastReply.toDate()) :
    message.lastReply instanceof Date ? DateTime.fromJSDate(message.lastReply) :
    undefined);
  return <div className="reply-indicator" onClick={() => props.doThread && props.doThread(message._id)}>
    <span>{message.replyCount} {label}</span>
    <span className="last-reply-time">
      {lastreply?.toRelative() || ""}
      <i className="fas fa-angle-right" />
    </span>
  </div>;
}

function makeButtons(msg: ContentMessage, props: MessageDisplayComponentProps): React.ReactElement<any, any> | null | false {
  // ButtonGroup doesn't add the proper classes to each Button if they're inside conditional statements
  // So we need to check for all the conditions and return the appropriate ButtonGroup
  // This covers the common cases
  if (!props.inThread && props.canWriteMessages && props.showEmojiPicker && props.doThread &&
      props.doRemoveMessage && props.doRemoveLocally) {
    return <ButtonGroup className="message-actions"
        size="small" variant="contained" color="secondary"
        aria-label="outlined primary button group">
      <Button onClick={(event) => props.showEmojiPicker && props.showEmojiPicker(event.currentTarget, props.msg)}
          disableRipple disableTouchRipple>
        <AddReactionIcon />
      </Button>
      <Button onClick={() => props.doThread && props.doThread(msg._id)}
          disableRipple disableTouchRipple>
        <ChatIcon />
      </Button>
      <Button className="delete-btn"
          onClick={() => props.doRemoveMessage && props.doRemoveMessage(msg._id, msg.frameID, msg.threadID)}
          disableRipple disableTouchRipple>
        <LocalFireDepartmentIcon />
      </Button>
      <Button className="delete-btn"
          onClick={() => props.doRemoveLocally && props.doRemoveLocally(msg._id, msg.frameID, msg.threadID)}
          disableRipple disableTouchRipple>
        <DeleteIcon />
      </Button>
    </ButtonGroup>;
  } else if (props.inThread && props.canWriteMessages && props.showEmojiPicker &&
      props.doRemoveMessage && props.doRemoveLocally) {
    return <ButtonGroup className="message-actions"
        size="small" variant="contained" color="secondary"
        aria-label="outlined primary button group">
      <Button onClick={(event) => props.showEmojiPicker && props.showEmojiPicker(event.currentTarget, props.msg)}
          disableRipple disableTouchRipple>
        <AddReactionIcon />
      </Button>
      <Button className="delete-btn"
          onClick={() => props.doRemoveMessage && props.doRemoveMessage(msg._id, msg.frameID, msg.threadID)}
          disableRipple disableTouchRipple>
        <LocalFireDepartmentIcon />
      </Button>
      <Button className="delete-btn"
          onClick={() => props.doRemoveLocally && props.doRemoveLocally(msg._id, msg.frameID, msg.threadID)}
          disableRipple disableTouchRipple>
        <DeleteIcon />
      </Button>
    </ButtonGroup>;
  } else if (!props.canWriteMessages && props.doRemoveLocally) {
    return <ButtonGroup className="message-actions"
        size="small" variant="contained" color="secondary"
        aria-label="outlined primary button group">
      <Button className="delete-btn"
          onClick={() => props.doRemoveLocally && props.doRemoveLocally(msg._id, msg.frameID, msg.threadID)}
          disableRipple disableTouchRipple>
        <DeleteIcon />
      </Button>
    </ButtonGroup>;
  }
  return <ButtonGroup className="message-actions"
      size="small" variant="contained" color="secondary"
      aria-label="outlined primary button group">
    {props.canWriteMessages && <>
      {props.showEmojiPicker &&
        <Button onClick={(event) => props.showEmojiPicker && props.showEmojiPicker(event.currentTarget, props.msg)}
            disableRipple disableTouchRipple>
          <AddReactionIcon />
        </Button>}
      {!props.inThread && props.doThread &&
        <Button onClick={() => props.doThread && props.doThread(msg._id)}
            disableRipple disableTouchRipple>
          <ChatIcon />
        </Button>}
      {props.doRemoveMessage &&
        <Button className="delete-btn"
            onClick={() => props.doRemoveMessage && props.doRemoveMessage(msg._id, msg.frameID, msg.threadID)}
            disableRipple disableTouchRipple>
          <LocalFireDepartmentIcon />
        </Button>}
    </>}
    {props.doRemoveLocally &&
      <Button className="delete-btn"
          onClick={() => props.doRemoveLocally && props.doRemoveLocally(msg._id, msg.frameID, msg.threadID)}
          disableRipple disableTouchRipple>
        <DeleteIcon />
      </Button>}
  </ButtonGroup>
}